import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import Icon from "js/react/components/Icon";
import { themeColors } from "js/react/sharedStyles";
import { Gap5 } from "./Gap";
import GuestBadge from "./GuestBadge";

const highlightText = (text, highlight) => {
    // Escape special characters for regex
    let regexHighlight = highlight.replace(/([.?*+^$[\]\\(){}|-])/g, "\\$1");
    // Split text on highlight term; include term itself into parts; ignore case
    let parts = text.split(new RegExp(`(${regexHighlight})`, "gi"));
    let result = (<>{
        parts.map((part, index) => {
            let className = part.toLowerCase() === highlight.toLowerCase() ? "highlighted" : "underlighted";
            return <span key={index} className={className}>{part}</span>;
        })
    }</>);
    return result;
};

export default function Avatar({ url, onClick = () => { }, className = "", id = undefined, style, ...rest }) {
    const [imageError, setImageError] = useState(false);

    useEffect(() => {
        setImageError(false);
    }, [url]);

    const handleImageError = e => {
        setImageError(true);
    };

    return (
        <div id={id} className={`avatar user ${className}`} onClick={onClick} style={style} {...rest}>
            {url && !imageError ? (
                <img src={url} alt="Avatar" onError={e => handleImageError(e)} style={{ width: "100%", height: "100%" }} />
            ) : (
                <Icon iconName="person" />
            )}
        </div>
    );
}

export function FolderAvatar({ url = null }) {
    const [imageError, setImageError] = useState(false);

    // Reset error state when URL changes
    useEffect(() => {
        setImageError(false);
    }, [url]);

    const handleImageError = () => {
        setImageError(true);
    };

    return (
        <div className="avatar folder" style={{ background: "none", color: themeColors.ui_blue }}>
            {
                url && !imageError
                    ? <img src={url} onError={handleImageError} />
                    : <Icon
                        style={{
                            fontSize: "34px",
                            lineHeight: "34px",
                            marginTop: "-2px",
                            width: "30px",
                            height: "30px",
                        }}
                        iconName="folder_shared"
                    />
            }
        </div>
    );
}

export function TeamAvatar({ url = null }) {
    const [imageError, setImageError] = useState(false);

    // Reset error state when URL changes
    useEffect(() => {
        setImageError(false);
    }, [url]);

    const handleImageError = () => {
        setImageError(true);
    };

    return (
        <div className="avatar team" style={{ background: "none", color: themeColors.ui_blue }}>
            {url && !imageError ? (
                <img src={url} onError={handleImageError} />
            ) : (
                <Icon iconName="business" />
            )}
        </div>
    );
}

export function UserProfile({ profile, highlight = undefined, className = undefined }) {
    const profileRef = useRef(null);

    const handleClickOutside = event => {
        if (profileRef.current && !profileRef.current.contains(event.target)) {
            window.getSelection().removeAllRanges();
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    let {
        photoURL,
        displayName,
        email,
        onClick,
        isPending,
        isGuest
    } = profile;

    if (highlight) {
        if (displayName) {
            displayName = highlightText(displayName, highlight);
        }
        if (email) {
            email = highlightText(email, highlight);
        }
    }

    return (
        <div ref={profileRef} className={`${className ?? ""} profile`} onClick={onClick}>
            <Avatar url={photoURL} />
            <div className={displayName ? "name-email" : "email-only"}>
                {
                    displayName
                        ? (<><div className="name can-trancate">{displayName}</div><div className="email">{email}</div></>)
                        : <div className="email can-trancate">{email}</div>
                }
                {
                    isPending &&
                    <div className="pending">Pending sign-up</div>
                }
            </div>
            {
                isGuest &&
                <>
                    <Gap5 />
                    <GuestBadge />
                </>
            }
        </div>
    );
}

export function FolderProfile({ profile, highlight }) {
    let {
        photoURL,
        name,
        isDefault = false,
        onClick,
    } = profile;

    if (highlight) {
        if (name) {
            name = highlightText(name, highlight);
        }
    }

    return (
        <div className="profile" onClick={onClick}>
            {
                isDefault
                    ? <TeamAvatar url={photoURL} />
                    : <FolderAvatar url={photoURL} />
            }
            <div className="name-email">
                <div className="name">{name}</div>
            </div>
        </div>
    );
}
